/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ElementRef,
  EventEmitter,
  HostBinding,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import {AsyncValidatorFn, UntypedFormControl} from '@angular/forms';
import {DomSanitizer} from '@angular/platform-browser';

import {TranslateService} from '@ngx-translate/core';

import {AsyncSubject, BehaviorSubject, EMPTY, merge, Observable, Subject} from 'rxjs';
import {catchError, distinctUntilChanged, filter, finalize, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';

import {ProfileMode} from '../../../../app.constants';
import {Destroyable} from '../../../../destroyable.component';
import {EmpContractAttributes} from '../../../../models/models.interfaces';
import {FileModel, getFileNameAndExtension, Preview} from '../../../../shared/utils/files';
import {Data} from '../../../api';
import {EmployeesService} from '../../../employees/employees.service';
import {LocalizationService} from '../../../localization/localization.service';
import {DataTransformer} from '../../data-transformer/data-transformer';
import {FieldType} from '../../fields/fields.interfaces';
import {FieldRegistry} from '../../fields/registry';
import {TextFieldComponent} from '../../fields/text-field/text-field.component';
import {FormFieldValidationComponent} from '../../form-field-validation/form-field-validation.component';
import {EmpZoneDependency, GroupZoneDescriptor, ZoneDescriptor, ZoneValues} from '../../form-generator.interfaces';
import {FormGeneratorService} from '../../form-generator.service';

@Component({
  selector: 'td [gp-form-table-cell]',
  templateUrl: 'form-table-cell.component.html',
  styleUrls: ['./form-table-cell.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class FormTableCellComponent extends Destroyable implements AfterViewInit, OnInit {
  public loaded: any = new EventEmitter();

  public descriptor: ZoneDescriptor;
  public value: any;
  public readOnly: boolean = false;
  public locale: string;
  public currency: string;
  public control: UntypedFormControl = null;
  public required: boolean = false;
  public groupZoneDescriptor: GroupZoneDescriptor;
  public loadedInternal: Subject<UntypedFormControl> = <Subject<UntypedFormControl>>new AsyncSubject<UntypedFormControl>();

  @ViewChild('fieldComponent', {read: ViewContainerRef, static: true})
  public fieldComponent: ViewContainerRef;

  @ViewChild(FormFieldValidationComponent, {static: true})
  public formFieldValidation: FormFieldValidationComponent;

  public row: any;
  public editMode: boolean = false;
  public fieldReady: boolean = false;
  public focused: boolean = false;
  public downloading: boolean = false;
  public groupData: ZoneValues;
  public isValueHidden: boolean = false;
  public isKey: boolean = false;
  public tableData: any;
  public employeeContracts: Array<Data<EmpContractAttributes>>;
  public isOnBoarding = false;
  public employeeId: number = null;
  public entityPath: string;

  public dependencyTriggered$: BehaviorSubject<EmpZoneDependency> = new BehaviorSubject<EmpZoneDependency>(null);

  public showViewDocumentPanel = false;
  public currentFileName: string;
  public reportDefinitionPreview: Preview;
  public previewError: boolean;
  public loadingPreview: boolean;
  public noSupportedFormat: boolean;
  public profileMode = ProfileMode.CLASSIC;
  public supportedFileExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp'];

  constructor(private element: ElementRef,
              private translate: TranslateService,
              private componentFactoryResolver: ComponentFactoryResolver,
              private formGeneratorService: FormGeneratorService,
              private changeDetectorRef: ChangeDetectorRef,
              private employeesService: EmployeesService,
              private registry: FieldRegistry,
              private localization: LocalizationService,
              private cd: ChangeDetectorRef,
              private sanitizer: DomSanitizer) {
    super();

    this.loaded = <Observable<UntypedFormControl>>this.loadedInternal;
  }

  public ngAfterViewInit(): any {
    this.init().then(control => {
      this.loadedInternal.next(control);
      this.loadedInternal.complete();
      this.changeDetectorRef.markForCheck();
    });

    if ((this.editMode && !this.descriptor.hidden || this.descriptor.type === 'FILE') && !this.fieldReady) {
      this.createField();
    }
    this.changeDetectorRef.markForCheck();

    this.row.switchEditMode
      .pipe(
        takeUntil(this.componentDestroyed$),
        distinctUntilChanged()
      )
      .subscribe((isEditMode: boolean) => {
        this.editMode = isEditMode;
        if (isEditMode && !this.descriptor.hidden && !this.fieldReady) {
          this.createField();
        }
        this.changeDetectorRef.markForCheck();
        this.focusField();
      });
  }

  public ngOnInit() {
    if (this.descriptor.type === 'CONTRACT' && !this.employeeContracts) {
      this.employeesService.selectedEmployeeContracts.pipe(
        takeUntil(this.componentDestroyed$)
      ).subscribe((value: Array<Data<EmpContractAttributes>>) => {
        this.employeeContracts = value;
      });
    }
    merge(
      this.localization.changeDateFormatEvent,
      this.localization.changeNumberFormatEvent
    )
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => this.changeDetectorRef.markForCheck());
  }

  public init() {
    if (this.value.type === FieldType.BOOLEAN) {
      this.value.value = (this.value.value === 'true' || this.value.value === true) ? true :
        (this.value.value === 'false' || this.value.value === false) ? false : null;
    }

    if (this.descriptor.inputMask) {
      this.value.value = DataTransformer.applyMask(this.value.value, this.descriptor.inputMask);
    }

    this.control = new UntypedFormControl(this.value.value);
    this.required = this.descriptor.isRequired;

    const validators = this.formFieldValidation.buildValidators();

    this.control.setValidators(validators);
    if (this.descriptor.zoneID === 'cliEmp.email' && !this.isOnBoarding) {
      const asyncValidators: AsyncValidatorFn[] = [];
      asyncValidators.push(this.employeesService.uniqueEmailAsyncValidator(this.employeeId));
      this.control.setAsyncValidators(asyncValidators);
    }
    this.control.updateValueAndValidity();

    if (this.value.type === 'ENTITY') {
      this.control.valueChanges.pipe(
        startWith(this.value.value),
        filter((value) => !!value),
        switchMap((value) => this.fetchRepEntityPath(value)),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
    }
    return Promise.resolve(this.control);
  }

  public fetchRepEntityPath(value): Observable<string> {
    return this.employeesService.getEntityPath(value).pipe(
      distinctUntilChanged(),
      tap((entityPath: string) => {
        this.entityPath = this.convertEntityPath(entityPath);
        this.changeDetectorRef.markForCheck();
      }),
      takeUntil(this.componentDestroyed$)
    );
  }

  private convertEntityPath(entityPath: string): string {
    if (entityPath) {
      const trimmedPath = entityPath.replace(/^\/|\/$/g, '');
      return trimmedPath.split('/').map((item) => item.replace(/_/g, ' ')).join(' >> ');
    }
    return '';
  }

  public get displayValue() {
    if (this.isValueHidden) {
      return '&nbsp;';
    }
    if (this.value.type !== 'CONTRACT') {
      return this.registry.getDisplayValue(this.value, this.descriptor, this.currency);
    } else {
      return this.registry.getDisplayValue(this.value, this.descriptor, this.currency, this.employeeContracts);
    }
  }

  public downloadFile(event) {
    event.stopImmediatePropagation();

    this.downloading = true;
    this.changeDetectorRef.markForCheck();

    this.employeesService
      .downloadFile(this.value.file, this.descriptor.metadatas.downloadFileUrl, this.descriptor.metadatas.fileUrlsAuthenticated)
      .pipe(
        finalize(() => {
          this.downloading = false;
          this.changeDetectorRef.markForCheck();
        })
      )
      .subscribe();
  }

  public getRepInheritedTooltip() {
    return this.descriptor.repInheritedValue.split(':')
      .filter(key => !!key)
      .map(key => this.translate.instant(key))
      .join(':');
  }

  public cellFocus(zoneId: string) {
    /* fake function. Will be overrided inside the  form-table-row-component */
  }

  public onCellClick(zoneId: string) {
    if (this.groupZoneDescriptor.groupZoneKeyName) {
      this.cellFocus(zoneId);
    } else {
      this.focused = true;
    }
  }

  private createField() {
    const component = this.registry.resolve(this.descriptor) || TextFieldComponent;

    const componentRef = this.fieldComponent.createComponent(
      this.componentFactoryResolver.resolveComponentFactory(component));

    Object.assign(componentRef.instance, {
      control: this.control,
      groupZoneDescriptor: this.groupZoneDescriptor,
      descriptor: this.descriptor,
      value: this.value,
      readOnly: this.readOnly || this.descriptor.readOnly,
      currency: this.currency,
      contracts: this.employeeContracts,
      locale: this.locale,
      parentScrollSelector: '.table-responsive',
      fieldFocused$: this.formGeneratorService.fieldFocused$,
      fieldBlurred$: this.formGeneratorService.fieldBlurred$,
      dependencyTriggered$: this.dependencyTriggered$,
      isViewAttachmentsActive: this.isViewAttachmentsActive()
    });

    if (componentRef.instance.init) {
      componentRef.instance.init();
    }

    this.fieldReady = true;
  }

  private focusField() {
    if (this.focused) {
      this.focused = false;

      setTimeout(() => {
        const btn = this.element.nativeElement.querySelector('.fa-calendar-alt, .btn');
        if (btn) {
          btn.click();
          return;
        }
        const input = this.element.nativeElement.querySelector('input, textarea');
        if (input) {
          input.focus();
          return;
        }
      });
    }
  }

  @HostBinding('style.display')
  public get display() {
    return this.descriptor.hidden ? 'none' : 'table-cell';
  }

  @HostBinding('style.border')
  public get border() {
    return 'none';
  }

  public viewFile(event): void {
    event.stopImmediatePropagation();

    if (!this.showViewDocumentPanel) {
      this.showViewDocumentPanel = true;
      this.previewDocument().subscribe();
    }
  }

  public isViewAttachmentsActive(): boolean {
    return !this.isOnBoarding;
  }

  public isSupportedFileType(fileName: string): boolean {
    const fileExtension = getFileNameAndExtension(fileName).fileExtension.toLowerCase();
    return this.supportedFileExtensions.includes(fileExtension);
  }

  public onCloseSidePanel() {
    this.showViewDocumentPanel = false;
    this.reportDefinitionPreview = null;
    this.currentFileName = '';
    this.previewError = false;
  }

  private previewDocument() {
    this.loadingPreview = true;
    this.currentFileName = 'placeholder text for skeleton text';

    return this.employeesService
      .getFile(this.value.file, this.descriptor.metadatas.downloadFileUrl, this.descriptor.metadatas.fileUrlsAuthenticated, this.profileMode)
      .pipe(
        tap((fileModel: FileModel) => {
          if (fileModel) {
            this.currentFileName = getFileNameAndExtension(fileModel.fileName).fileName;
            if ((navigator as any).msSaveBlob) {
              (navigator as any).msSaveBlob(fileModel.blob, fileModel.fileName);
            } else {
              const supportedFileFormatTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp'];
              this.noSupportedFormat = !supportedFileFormatTypes.includes(fileModel.blob?.type);
              this.reportDefinitionPreview = this.getDocumentPreview(URL.createObjectURL(fileModel.blob), fileModel.fileName);
              this.previewError = false;
            }
          } else {
            this.previewError = true;
            this.reportDefinitionPreview = null;
            this.currentFileName = '';
          }
        }),
        catchError(() => {
          this.previewError = true;
          this.reportDefinitionPreview = null;
          this.currentFileName = '';
          return EMPTY;
        }),
        finalize(() => {
          this.loadingPreview = false;
          this.cd.detectChanges();
        })
      );
  }

  private getDocumentPreview(href: string, fileName: string): Preview {
    return {
      iframe: this.sanitizer.bypassSecurityTrustHtml(`
             <iframe width="100%" height="100%" src="${href}"
                     name="${fileName}">
             </iframe>
          `), href: this.sanitizer.bypassSecurityTrustUrl(href), download: fileName
    };
  }
}
